import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Camera, Check, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, fileUrl } from "../lib/api";

export const PhotoRequestsButton = ({ onChanged }) => {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.listPhotoRequests(); setItems(r.items || []); setCount(r.count || 0); }
    catch { setItems([]); setCount(0); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const openDialog = async () => { setOpen(true); setLoading(true); await load(); setLoading(false); };

  const act = async (emp, approve) => {
    setBusyId(emp.id);
    try {
      if (approve) await api.approveEmployeePhoto(emp.id); else await api.rejectEmployeePhoto(emp.id);
      toast.success(approve ? "تمت الموافقة على الصورة" : "تم رفض الصورة");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تنفيذ العملية"); }
    finally { setBusyId(null); }
  };

  if (count === 0 && !open) return null;

  return (
    <>
      {count > 0 && (
        <button data-testid="photo-requests-badge" onClick={openDialog} title="طلبات صور بانتظار الموافقة"
          className="relative flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 h-9 text-amber-700 hover:bg-amber-100 transition-colors">
          <Camera className="h-4 w-4" />
          <span className="hidden sm:inline text-xs font-semibold">طلبات صور</span>
          <span data-testid="photo-requests-count" className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center">{count}</span>
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md font-body" dir="rtl" data-testid="photo-requests-dialog">
          <DialogHeader className="text-right">
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Camera className="h-5 w-5 text-amber-500" /> طلبات تغيير الصور الشخصية
            </DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">لا توجد طلبات بانتظار الموافقة</div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {items.map((it) => (
                <div key={it.id} data-testid={`photo-request-${it.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <img src={fileUrl(it.pending_photo_id)} alt="pending" className="h-16 w-16 rounded-xl object-cover border border-slate-200 bg-slate-50 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-slate-800 text-sm truncate">{it.name}</p>
                    <p className="text-xs text-slate-400 truncate">{it.branch || "—"}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button size="sm" data-testid={`photo-approve-${it.id}`} disabled={busyId === it.id} onClick={() => act(it, true)}
                      className="h-8 gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                      {busyId === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} موافقة
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`photo-reject-${it.id}`} disabled={busyId === it.id} onClick={() => act(it, false)}
                      className="h-8 gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50">
                      <X className="h-3.5 w-3.5" /> رفض
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
